import { Button } from 'antd';
import { CheckCircleOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /features/reports - prerendered feature page targeting searches like
 * "school report card software", "ZIMSEC grading system", and
 * "end of term reports Zimbabwe".
 */
export const ReportsFeaturePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/reports">
      <SEO
        title="School Report Card Software - ZIMSEC and Cambridge Grading | Rongeka Africa"
        description="Capture marks, apply the grading scale your school uses - ZIMSEC style, Cambridge style, or your own - and deliver end-of-term report cards to parents on WhatsApp."
        url="https://rongeka.africa/features/reports"
        keywords="school report card software, ZIMSEC grading system, Cambridge grading school software, end of term reports Zimbabwe, results management system, school assessment software"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">RESULTS AND REPORT CARDS</div>
        <h1 className="mkt-hero-title">End-of-Term Report Cards in Days, Not Weeks</h1>
        <p className="mkt-hero-lead">
          Teachers enter marks once. Rongeka applies your grading scale, builds the report
          cards, and delivers them to parents on WhatsApp - for primary schools, O Level,
          and A Level classes alike.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Grading that matches your curriculum</h2>
          <p className="mkt-section-body">
            Whether your school follows the Zimbabwean curriculum with ZIMSEC-style grading,
            runs Cambridge classes, or uses its own internal scale, Rongeka applies the right
            grade boundaries automatically. Set up different scales for different levels -
            junior school, O Level, A Level - and marks convert to grades consistently across
            every class and every teacher.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Configurable grading scales per level and subject</li>
            <li><CheckCircleOutlined />ZIMSEC-style and Cambridge-style grade boundaries</li>
            <li><CheckCircleOutlined />Consistent grading across every teacher's mark book</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">From mark entry to parent's phone</h2>
          <ol className="mkt-steps">
            <li>
              <strong>Teachers capture marks</strong>
              Each teacher enters assessment and exam marks for their own subjects, from the
              portal or their phone.
            </li>
            <li>
              <strong>Rongeka builds the reports</strong>
              Grades, class positions, teacher comments, and attendance come together on a
              clean report card, term after term.
            </li>
            <li>
              <strong>Parents get them on WhatsApp</strong>
              Report cards go out to every guardian at once - no printing backlog, no
              envelopes that never make it home.
            </li>
          </ol>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">A complete academic record for every student</h2>
          <p className="mkt-section-body">
            Every mark and every term report stays on the student's record, so progress is
            visible across terms and years - not buried in last year's files. Heads see
            class and subject performance at a glance, and when a parent asks how their
            child is really doing, the answer is one WhatsApp message away.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Results history across terms and academic years</li>
            <li><CheckCircleOutlined />Class and subject performance analytics for school heads</li>
            <li><CheckCircleOutlined />Parents check results any time on WhatsApp</li>
          </ul>
          <div className="mkt-hero-actions">
            <Button
              size="large"
              icon={<WhatsAppOutlined />}
              onClick={() => window.open('https://wa.me/263789442202?text=hi', '_blank')}
            >
              See It on WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};
